import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, fieldClass } from '../discovery/fields';
import { WorkingHoursFields, normalizeWorkingHours, workingHoursProblem } from '../discovery/workingHours';

const button =
  'inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium transition disabled:opacity-50';

function sameList(left, right) {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

const SessionSettingsModal = ({ session, onClose, onSaved }) => {
  const [hours, setHours] = useState(() => normalizeWorkingHours(session.workingHours));
  const [intent, setIntent] = useState(session.intent || '');
  const [exclusions, setExclusions] = useState((session.exclusions || []).join(', '));
  const [pending, setPending] = useState(false);
  const problem = workingHoursProblem(hours);
  const nextIntent = intent.trim();
  const intentChanged = nextIntent !== (session.intent || '').trim();
  const intentProblem = intentChanged && (nextIntent.length < 8 || nextIntent.length > 600);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pending]);

  const save = async (event) => {
    event.preventDefault();
    if (problem || intentProblem) return;
    setPending(true);
    try {
      const nextExclusions = exclusions
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const body = { workingHours: hours };
      // Saving the company description rebuilds the living document. Leave it
      // out unless it actually changed, so an hours edit keeps the summary.
      if (intentChanged) body.intent = nextIntent;
      if (!sameList(nextExclusions, session.exclusions || [])) body.exclusions = nextExclusions;
      await axios.post(`${API_BASE_URL}/api/admin/discovery/sessions/${session.id}`, body);
      toast.success('Session settings saved');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'The settings could not be saved.');
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-10">
      <div
        className="w-full max-w-xl rounded-xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-settings-title"
      >
        <form onSubmit={save}>
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 id="session-settings-title" className="text-lg font-semibold text-gray-900">
              Session settings
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              What the company does is the lens for the summary. Excluded applications are left out of the recording.
              Hours apply to every computer on {session.companyName}, and a computer can override them on its own page.
            </p>
          </div>
          <div className="space-y-5 px-5 py-4">
            <div>
              <label htmlFor="session-intent" className="block text-sm font-medium text-gray-900">
                What the company does
              </label>
              <textarea
                id="session-intent"
                rows={3}
                className={fieldClass}
                value={intent}
                maxLength={600}
                onChange={(event) => setIntent(event.target.value)}
                placeholder="Wholesale distributor. Orders are taken, picked, and invoiced the same day."
              />
              <p className="mt-1 text-sm text-gray-500">The summary uses this as its lens. A sentence or two.</p>
              {intentProblem ? (
                <p className="mt-1 text-sm text-red-700">Describe what the company does, in a sentence or two.</p>
              ) : null}
            </div>
            <div>
              <label htmlFor="session-exclusions" className="block text-sm font-medium text-gray-900">
                Excluded applications
              </label>
              <input
                id="session-exclusions"
                className={fieldClass}
                value={exclusions}
                placeholder="WhatsApp, Spotify"
                onChange={(event) => setExclusions(event.target.value)}
              />
              <p className="mt-1 text-sm text-gray-500">
                The observer records every application in front. Name any to leave out, separated by commas.
              </p>
            </div>
            <WorkingHoursFields value={hours} onChange={setHours} idPrefix="session-hours" />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
            <button type="button" className={`${button} text-gray-600 hover:bg-gray-100`} disabled={pending} onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={pending || Boolean(problem) || intentProblem}
              className={`${button} bg-primary-600 text-white hover:bg-primary-700`}
            >
              {pending ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionSettingsModal;
